import { UserOutlined } from "@ant-design/icons"
import { Avatar, Flex, Progress, Typography } from "antd"
import { type ColumnsType } from "antd/es/table"
import type { BaseOptionType } from "antd/lib/select"
import React from "react"
import { Link } from "react-router-dom"

import { RecordRef } from "../../../../components/RecordRef.tsx"
import { CreatedCell } from "../../../../components/Table/CreatedCell.tsx"
import { empty } from "../../../../constants/placeholders"
import { RECORD_ID_LABEL } from "../../../../constants/record-ref.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useDateFormatter } from "../../../../hooks/use-date-formatter.tsx"
import { readAccountInstant } from "../../../../utils/date/read-account-instant.ts"
import { shortRelativeTime } from "../../../../utils/date/short-relative-time.ts"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { getEntityName } from "../../../../utils/user/get-entity-name"
import { dateFormat } from "../../../../utils/utils"
import {
  ProjectStatusSelector,
  type StatusChangeOptions,
  type StatusChangeOutcome
} from "../../../Project/components/ProjectStatusSelector.tsx"
import { ProjectStatusTag } from "../../../Project/components/ProjectStatusTag.tsx"
import { UnpaidInvoicesMarker } from "../../components/UnpaidInvoicesMarker.tsx"
import {
  canChangeProjectStatus,
  canCloseProject
} from "../../../Project/helpers/project-edit-visibility.ts"
import { type PermissionProjectPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import { creatorLabel } from "../../constants/created.ts"
import {
  getProjectCustomerName,
  normalizePercent
} from "../../constants/helpers.ts"
import { getProgressColor } from "../../constants/project-progress.ts"
import { type ProjectListItem } from "../../types/types.ts"
import { type ProjectManager } from "../../../Project/types/project.types.ts"
import { getProjectsColumnActions } from "./column-actions.tsx"

const { Text } = Typography

const renderPeople = (people: ProjectManager[] | null | undefined) => {
  if (!people?.length) return <Text type="secondary">{empty}</Text>

  return (
    <Flex vertical gap={4}>
      {people.map((person) => {
        const name = getEntityName(person, "No name provided")

        return (
          <Flex key={person?.id} gap={8} align="center">
            <Avatar
              className={"shrink-0"}
              size="small"
              src={person?.avatar}
              icon={!person.avatar && <UserOutlined />}
            />
            <Text className={"min-w-0"} ellipsis={{ tooltip: name }}>
              {name}
            </Text>
          </Flex>
        )
      })}
    </Flex>
  )
}

// `archived_at` arrives rendered in the account's own format and zone (a
// DD/MM account sends "12/10/2026 14:05"); a bare `dayjs(value)` reads it
// month-first, so 12 October showed as 10 December and the 13th on as
// "Invalid Date".
function ArchivedOnCell({ value }: { value: string | null }) {
  const { timezone, dateFormat: accountFormat } = useDateFormatter()
  const at = readAccountInstant(value, accountFormat, timezone)
  if (!at) return <Text type="secondary">{empty}</Text>
  return <Text>{at.format(dateFormat)}</Text>
}

// Shared with ProjectListEditableRow, which matches its editors to these
// columns by key.
export const PROJECT_COLUMN_KEYS = {
  uid: "uid",
  customer: "customer",
  leadName: "lead_name",
  projectManagers: "project_managers",
  salesManagers: "sales_managers",
  endDate: "end_date",
  projectStatus: "project_status",
  created: "created",
  createdBy: "created_by",
  lastActivity: "updated",
  salesPrice: "sales_price",
  progress: "progress",
  archivedAt: "archived_at",
  archiveReason: "archive_reason",
  scheduleHealth: "schedule_health",
  nextMilestone: "next_milestone"
} as const

// const progressColors: Record<ProjectStatus, string> = {
//   Completed: "#52c41a",
//   "In Progress": "#1890ff",
//   Active: "#1890ff",
//   Overdue: "#ff4d4f",
//   "On Hold": "#d9d9d9",
//   Archived: "#8c8c8c"
// }

interface GetColumnsArg {
  t: (key: string) => string
  statuses: BaseOptionType[]
  projectPermissions: PermissionProjectPermissions | undefined
  showProgress: boolean
  showBudget: boolean
  editAllowed: boolean
  archived?: boolean
  archiveAllowed?: boolean
  restoreAllowed?: boolean
  deleteAllowed?: boolean
  onStatusChange: (
    id: string,
    status: number,
    options?: StatusChangeOptions
  ) => Promise<StatusChangeOutcome>
  onSelectRow: (record: ProjectListItem, action: string) => Promise<void> | void
}

export const getColumns = ({
  t,
  statuses,
  projectPermissions,
  showProgress,
  showBudget,
  editAllowed,
  archived = false,
  archiveAllowed = false,
  restoreAllowed = false,
  deleteAllowed = false,
  onStatusChange,
  onSelectRow
}: GetColumnsArg): ColumnsType<ProjectListItem> => [
  {
    title: RECORD_ID_LABEL,
    dataIndex: "uid",
    key: PROJECT_COLUMN_KEYS.uid,
    width: 150,
    render: (uid: string | null) => <RecordRef uid={uid} compact />
  },
  {
    // Sorts on the customer the row shows, which is the same rule the customer
    // filter beside it matches on.
    title: t("filters.customerLabel"),
    dataIndex: "customer_name",
    key: PROJECT_COLUMN_KEYS.customer,
    sorter: true,
    width: 160,
    render: (_: unknown, project: ProjectListItem) => {
      const fullName = getProjectCustomerName(project)
      return <Text ellipsis={{ tooltip: fullName }}>{fullName || empty}</Text>
    }
  },
  {
    title: "Project Name",
    dataIndex: "lead_name",
    key: PROJECT_COLUMN_KEYS.leadName,
    sorter: true,
    width: 210,
    render: (value: string, project) => (
      <Link to={RoutesPaths.project.view(project?.id).overview}>
        {archived && (project?.unpaid_invoice_count ?? 0) > 0 ? (
          <UnpaidInvoicesMarker count={project.unpaid_invoice_count as number}>
            {value}
          </UnpaidInvoicesMarker>
        ) : (
          <Text className={"text-inherit"} ellipsis={{ tooltip: value }}>
            {value}
          </Text>
        )}
      </Link>
    )
  },
  {
    title: "Project Manager",
    dataIndex: "project_managers",
    key: PROJECT_COLUMN_KEYS.projectManagers,
    sorter: true,
    width: 180,
    render: (managers: ProjectListItem["project_managers"]) =>
      renderPeople(managers)
  },
  {
    // Only the Archive's rows carry it.
    hidden: !archived,
    title: t("projectArchive.columns.salesManager"),
    dataIndex: "sales_managers",
    key: PROJECT_COLUMN_KEYS.salesManagers,
    width: 180,
    render: (managers: ProjectListItem["sales_managers"]) =>
      renderPeople(managers)
  },
  {
    title: "Due Date",
    dataIndex: "end_date",
    key: PROJECT_COLUMN_KEYS.endDate,
    width: 130,
    sorter: true,
    render: (value: string) => (
      <Text ellipsis={{ tooltip: value }}>{value || empty}</Text>
    )
  },
  {
    title: "Status",
    dataIndex: "project_status",
    key: PROJECT_COLUMN_KEYS.projectStatus,
    width: 170,
    // filters: [
    //   { text: "Active", value: "Active" },
    //   { text: "In Progress", value: "In Progress" },
    //   { text: "Completed", value: "Completed" },
    //   { text: "On Hold", value: "On Hold" },
    //   { text: "Overdue", value: "Overdue" },
    //   { text: "Archived", value: "Archived" }
    // ],
    render: (status: string, record) =>
      !archived && canChangeProjectStatus(projectPermissions, record) ? (
        <ProjectStatusSelector
          status={status}
          statuses={statuses}
          canClose={canCloseProject(projectPermissions, record)}
          onChange={(newStatus, options) =>
            onStatusChange(record?.id, newStatus, options)
          }
        />
      ) : (
        <ProjectStatusTag status={status} />
      )
  },
  {
    title: "Created Date",
    dataIndex: "created",
    key: PROJECT_COLUMN_KEYS.created,
    width: 130,
    align: "center",
    sorter: true,
    sortDirections: ["descend", "ascend"],
    render: (value: string | null) => <CreatedCell value={value} />
  },
  {
    title: "Created By",
    dataIndex: "created_by",
    key: PROJECT_COLUMN_KEYS.createdBy,
    width: 150,
    align: "center",
    render: (value: string | null) => (
      <Text ellipsis={{ tooltip: creatorLabel(value) }}>
        {creatorLabel(value)}
      </Text>
    )
  },
  {
    title: t("projectsTable.columns.lastActivity"),
    dataIndex: "updated",
    key: PROJECT_COLUMN_KEYS.lastActivity,
    width: 110,
    sorter: true,
    render: (value: string | null) => {
      const relative = shortRelativeTime(value)
      if (!relative) return <Text type="secondary">{empty}</Text>
      return <Text ellipsis={{ tooltip: value }}>{relative}</Text>
    }
  },
  {
    hidden: !showBudget,
    title: "Sales Price",
    dataIndex: "sales_price",
    key: PROJECT_COLUMN_KEYS.salesPrice,
    width: 120,
    sorter: true,
    render: (value: number | null) => {
      const formatted = budgetFormat(value)
      return <Text ellipsis={{ tooltip: formatted }}>{formatted}</Text>
    }
  },
  {
    hidden: !showProgress,
    title: "Progress",
    dataIndex: "progress",
    key: PROJECT_COLUMN_KEYS.progress,
    width: 150,
    // sorter: true,
    render: (value: string) => {
      const progressValue = normalizePercent(value || "0%")
      return (
        <Progress
          strokeColor={getProgressColor(progressValue)}
          percent={progressValue}
          size="small"
        />
      )
    }
  },
  {
    hidden: !archived,
    title: t("projectArchive.columns.archivedOn"),
    dataIndex: "archived_at",
    key: PROJECT_COLUMN_KEYS.archivedAt,
    width: 200,
    sorter: true,
    render: (value: string | null) => <ArchivedOnCell value={value} />
  },
  {
    hidden: !archived,
    title: t("projectArchive.columns.reason"),
    dataIndex: "archive_reason_name",
    key: PROJECT_COLUMN_KEYS.archiveReason,
    width: 140,
    render: (value: string | null) =>
      value ? <Text>{value}</Text> : <Text type="secondary">{empty}</Text>
  },
  getProjectsColumnActions({
    t,
    editAllowed: editAllowed && !archived,
    // Archive puts a project in; Restore takes it out. Each belongs to the
    // tab that can perform it.
    archiveAllowed: archiveAllowed && !archived,
    restoreAllowed: restoreAllowed && archived,
    deleteAllowed: deleteAllowed && archived,
    onSelectRow
  })
]
